"use client";

import React, { useMemo, useState } from "react";
import { useEvmWallet } from "../../components/EvmWalletProvider";

type Props = {
  onError?: (e: unknown) => void;
};

type WalletOption = {
  id: string;
  label: string;
  eyebrow: string;
  chipBg: string;
  chipColor: string;
  Icon: React.FC;
  onClick: () => Promise<void>;
  available: boolean;
  unavailableNote?: string;
};

function isMobileDevice() {
  if (typeof navigator === "undefined") return false;
  return /Mobi|Android|iPhone|iPad|iPod/i.test(navigator.userAgent);
}

type InjectedProviderInfo = {
  isMetaMask?: boolean;
  isCoinbaseWallet?: boolean;
  isTrust?: boolean;
  isTrustWallet?: boolean;
  providers?: InjectedProviderInfo[];
};

function getInjectedEthereum(): InjectedProviderInfo | undefined {
  if (typeof window === "undefined") return undefined;
  return (window as unknown as { ethereum?: InjectedProviderInfo }).ethereum;
}

function hasInjectedEthereum() {
  return !!getInjectedEthereum();
}

function hasMetaMaskInjected() {
  const eth = getInjectedEthereum();
  if (!eth) return false;
  if (eth.isMetaMask) return true;
  const providers = Array.isArray(eth.providers) ? eth.providers : [];
  return providers.some((p) => p?.isMetaMask);
}

function hasCoinbaseInjected() {
  const eth = getInjectedEthereum();
  if (!eth) return false;
  if (eth.isCoinbaseWallet) return true;
  const providers = Array.isArray(eth.providers) ? eth.providers : [];
  return providers.some((p) => p?.isCoinbaseWallet);
}

function hasTrustWalletInjected() {
  const eth = getInjectedEthereum();
  if (!eth) return false;
  if (eth.isTrust || eth.isTrustWallet) return true;
  const providers = Array.isArray(eth.providers) ? eth.providers : [];
  return providers.some((p) => p?.isTrust || p?.isTrustWallet);
}

function buildMetaMaskDeepLink(currentUrl: string) {
  // MetaMask expects: https://metamask.app.link/dapp/<dapp-url-without-protocol>
  // Example: https://metamask.app.link/dapp/example.com/login
  const withoutProtocol = currentUrl.replace(/^https?:\/\//i, "");
  return `https://metamask.app.link/dapp/${withoutProtocol}`;
}

function buildCoinbaseDeepLink(currentUrl: string) {
  // Coinbase Wallet deep link format.
  // Ref: https://go.cb-w.com/dapp?cb_url=<encoded_url>
  return `https://go.cb-w.com/dapp?cb_url=${encodeURIComponent(currentUrl)}`;
}

function buildTrustWalletDeepLink(currentUrl: string) {
  // Trust Wallet universal deep link.
  // coin_id=60 is Ethereum; still works for opening the dapp browser.
  return `https://link.trustwallet.com/open_url?coin_id=60&url=${encodeURIComponent(currentUrl)}`;
}

// ── Compact, abstract per-wallet glyphs (deliberately generic rather than
// recreations of trademarked logos) — differentiation comes from the chip
// tint + shape, while every row otherwise shares one neutral card style. ──
function IconDiamond() {
  return (
    <svg width="17" height="17" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg" aria-hidden="true">
      <path d="M12 3L20 9.5L12 21L4 9.5L12 3Z" stroke="currentColor" strokeWidth="1.7" strokeLinejoin="round" />
      <path d="M4 9.5H20M12 3V21" stroke="currentColor" strokeWidth="1.2" opacity="0.6" />
    </svg>
  );
}

function IconRing() {
  return (
    <svg width="17" height="17" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg" aria-hidden="true">
      <circle cx="12" cy="12" r="8.5" stroke="currentColor" strokeWidth="1.7" />
      <rect x="8.8" y="8.8" width="6.4" height="6.4" rx="1.6" fill="currentColor" />
    </svg>
  );
}

function IconShieldSmall() {
  return (
    <svg width="17" height="17" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg" aria-hidden="true">
      <path d="M12 3L19 6V11.3C19 15.8 15.9 19.7 12 21C8.1 19.7 5 15.8 5 11.3V6L12 3Z" stroke="currentColor" strokeWidth="1.7" strokeLinejoin="round" />
    </svg>
  );
}

function IconSwap() {
  return (
    <svg width="17" height="17" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg" aria-hidden="true">
      <path d="M6 8H16.5L14 5.5" stroke="currentColor" strokeWidth="1.7" strokeLinecap="round" strokeLinejoin="round" />
      <path d="M18 16H7.5L10 18.5" stroke="currentColor" strokeWidth="1.7" strokeLinecap="round" strokeLinejoin="round" />
    </svg>
  );
}

function IconChevron() {
  // Uses Tailwind's `group`/`group-hover` (global utility CSS) rather than the
  // wrapping component's scoped <style jsx> rules, since those only reach
  // elements written literally inside that component's own JSX tree — not
  // elements rendered by a separate child component like this one.
  return (
    <svg
      width="16"
      height="16"
      viewBox="0 0 24 24"
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
      aria-hidden="true"
      className="shrink-0 text-slate-500 transition-all duration-150 group-hover:translate-x-0.5 group-hover:text-[color:var(--brand-cyan,#00f5ff)]"
    >
      <path d="M9 6L15 12L9 18" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" />
    </svg>
  );
}

function IconSpinner() {
  return <span className="h-4 w-4 shrink-0 animate-spin rounded-full border-2 border-white/25 border-t-white" />;
}

function IconPhone() {
  return (
    <svg width="17" height="17" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg" aria-hidden="true">
      <rect x="7" y="2.5" width="10" height="19" rx="2.4" stroke="currentColor" strokeWidth="1.7" />
      <path d="M10.5 18.2H13.5" stroke="currentColor" strokeWidth="1.7" strokeLinecap="round" />
    </svg>
  );
}

export default function WalletButtons({ onError }: Props) {
  const { connectMetaMask, connectCoinbase, connectTrustWallet, connectUniswapWallet, isConnecting } = useEvmWallet();
  const [loadingId, setLoadingId] = useState<string | null>(null);
  const [showWalletBrowser, setShowWalletBrowser] = useState(false);
  const hasWalletConnect = !!process.env.NEXT_PUBLIC_WALLETCONNECT_PROJECT_ID;
  const isMobile = isMobileDevice();

  const isBusy = useMemo(() => isConnecting || !!loadingId, [isConnecting, loadingId]);

  const walletOptions: WalletOption[] = [
    {
      id: "metamask",
      label: "MetaMask",
      eyebrow: isMobile && !hasMetaMaskInjected() ? "Opens the MetaMask app" : "Browser extension",
      chipBg: "rgba(246, 133, 27, 0.16)",
      chipColor: "#FFB463",
      Icon: IconDiamond,
      onClick: handleConnectMetaMask,
      available: true,
    },
    {
      id: "coinbaseWallet",
      label: "Coinbase Wallet",
      eyebrow: isMobile && !hasCoinbaseInjected() ? "Opens the Coinbase app" : "Smart wallet",
      chipBg: "rgba(0, 82, 255, 0.16)",
      chipColor: "#6FA0FF",
      Icon: IconRing,
      onClick: handleConnectCoinbase,
      available: true,
    },
    {
      id: "trustWallet",
      label: "Trust Wallet",
      eyebrow: isMobile && !hasTrustWalletInjected() ? "Opens the Trust app" : "Mobile-first",
      chipBg: "rgba(16, 185, 129, 0.16)",
      chipColor: "#5EEAC0",
      Icon: IconShieldSmall,
      onClick: handleConnectTrustWallet,
      available: true,
    },
    {
      id: "uniswap",
      label: "Uniswap Wallet",
      eyebrow: hasWalletConnect ? "Via WalletConnect" : "Not configured",
      chipBg: "rgba(255, 0, 122, 0.16)",
      chipColor: "#FF7FB8",
      Icon: IconSwap,
      onClick: handleConnectUniswapWallet,
      available: hasWalletConnect,
      unavailableNote: "WalletConnect isn't configured for this deployment yet.",
    },
  ];

  const busyLabel = loadingId
    ? walletOptions.find((option) => option.id === loadingId)?.label || 'wallet'
    : null;

  // Deep-linking to a wallet app is a bare `location.href` navigation, not a
  // promise - there's no callback for "the app didn't open." If the wallet
  // app isn't installed, or iOS/Android silently blocks the redirect, the
  // page just sits there with the button reset and no explanation, which is
  // exactly the "sometimes it just goes pending, and I have to open the
  // MetaMask browser myself" complaint. A tab switching to another app
  // reliably fires `visibilitychange` (hidden); if the page is *still*
  // visible after a couple seconds, the deep link almost certainly failed,
  // so tell the user instead of leaving them stuck.
  function deepLinkWithFallback(deepLink: string, walletLabel: string) {
    try {
      window.localStorage.setItem("walletLoginPending", "true");
    } catch {
      // ignore
    }
    window.location.href = deepLink;
    window.setTimeout(() => {
      if (document.visibilityState === "visible") {
        onError?.(
          new Error(
            `Couldn't open the ${walletLabel} app automatically. Make sure it's installed, or use "Open in wallet app" below to try again.`
          )
        );
      }
    }, 1800);
  }

  async function handleConnectMetaMask() {
    try {
      setLoadingId("metamask");

      const isMobile = isMobileDevice();
      const hasInjected = hasInjectedEthereum();
      const isMetaMask = hasMetaMaskInjected();

      // Mobile + no injected provider: deep link into MetaMask app.
      if (isMobile && (!hasInjected || !isMetaMask)) {
        deepLinkWithFallback(buildMetaMaskDeepLink(window.location.href), "MetaMask");
        return;
      }

      await connectMetaMask();
    } catch (e) {
      onError?.(e);
    } finally {
      setLoadingId(null);
    }
  }

  async function handleConnectCoinbase() {
    try {
      setLoadingId("coinbaseWallet");

      const isMobile = isMobileDevice();
      const hasInjected = hasInjectedEthereum();
      const isCoinbase = hasCoinbaseInjected();

      // Mobile + no injected Coinbase provider: deep link into Coinbase Wallet.
      // This provides a smoother UX than trying to connect from a regular mobile browser.
      if (isMobile && (!hasInjected || !isCoinbase)) {
        deepLinkWithFallback(buildCoinbaseDeepLink(window.location.href), "Coinbase Wallet");
        return;
      }

      await connectCoinbase();
    } catch (e) {
      onError?.(e);
    } finally {
      setLoadingId(null);
    }
  }

  async function handleConnectTrustWallet() {
    try {
      setLoadingId("trustWallet");

      const isMobile = isMobileDevice();
      const hasInjected = hasInjectedEthereum();
      const isTrust = hasTrustWalletInjected();

      // Mobile + no injected Trust provider: deep link into Trust Wallet.
      if (isMobile && (!hasInjected || !isTrust)) {
        deepLinkWithFallback(buildTrustWalletDeepLink(window.location.href), "Trust Wallet");
        return;
      }

      await connectTrustWallet();
    } catch (e) {
      onError?.(e);
    } finally {
      setLoadingId(null);
    }
  }

  async function handleConnectUniswapWallet() {
    try {
      setLoadingId('uniswap');
      await connectUniswapWallet();
    } catch (e) {
      onError?.(e);
    } finally {
      setLoadingId(null);
    }
  }

  return (
    <div className="wallet-login-options space-y-2.5">
      {walletOptions.map((option) => {
        const active = loadingId === option.id;
        const { Icon } = option;
        return (
          <button
            key={option.id}
            type="button"
            className={`wallet-row group flex w-full items-center gap-3.5 rounded-2xl border px-4 py-3.5 text-left transition-all active:scale-[0.985] ${
              active ? 'wallet-row-active' : ''
            } ${!option.available ? 'wallet-row-disabled' : ''}`}
            onClick={option.onClick}
            disabled={isBusy || !option.available}
            title={!option.available ? option.unavailableNote : undefined}
          >
            <span
              className="flex h-10 w-10 shrink-0 items-center justify-center rounded-xl"
              style={{ background: option.chipBg, color: option.chipColor }}
            >
              <Icon />
            </span>
            <span className="min-w-0 flex-1">
              <span className="wallet-row-label block text-sm font-semibold">{option.label}</span>
              <span className="wallet-row-eyebrow mt-0.5 block text-[11px]">{option.eyebrow}</span>
            </span>
            {active ? (
              <IconSpinner />
            ) : !option.available ? (
              <span className="wallet-row-soon shrink-0 rounded-full border px-2 py-0.5 text-[10px] font-semibold uppercase tracking-[0.14em]">
                Soon
              </span>
            ) : (
              <IconChevron />
            )}
          </button>
        );
      })}

      {busyLabel ? (
        <div className="wallet-status rounded-xl border px-4 py-3 text-[13px]">
          Opening <span className="wallet-row-label font-semibold">{busyLabel}</span>. Approve the connection request in your wallet to continue.
        </div>
      ) : null}

      {/* Client request: explicit 'Open in wallet browser' entry point for mobile.
          Styled to match the wallet rows above (icon chip + label + eyebrow)
          so the mobile-only entry point reads as part of the same system
          instead of a bolted-on extra element. */}
      {isMobile && (
        <button
          type="button"
          className="wallet-row flex w-full items-center gap-3.5 rounded-2xl border px-4 py-3.5 text-left transition-all active:scale-[0.985]"
          onClick={() => setShowWalletBrowser(true)}
          disabled={isBusy}
        >
          <span className="flex h-10 w-10 shrink-0 items-center justify-center rounded-xl" style={{ background: 'rgba(0, 245, 255, 0.14)', color: '#7fe3ff' }}>
            <IconPhone />
          </span>
          <span className="min-w-0 flex-1">
            <span className="wallet-row-label block text-sm font-semibold">Open in wallet app</span>
            <span className="wallet-row-eyebrow mt-0.5 block text-[11px] leading-relaxed">Recommended on mobile for the smoothest reconnect and signature flow.</span>
          </span>
          <span className="wallet-row-tag hidden shrink-0 rounded-full border px-2.5 py-1 text-[10px] font-semibold uppercase tracking-[0.14em] xs:inline-flex">
            Recommended
          </span>
        </button>
      )}

      {isMobile && (
        <p className="wallet-row-eyebrow px-1 text-center text-[13px] leading-relaxed">
          On mobile, your wallet may open in a separate app. After approving, return here to continue.
        </p>
      )}

      {showWalletBrowser && typeof window !== 'undefined' && (
        <div className="fixed inset-0 z-[2000] flex items-end justify-center sm:items-center">
          <div
            className="wallet-modal-backdrop absolute inset-0 bg-black/70 backdrop-blur-sm"
            onClick={() => setShowWalletBrowser(false)}
            aria-hidden="true"
          />
          <div
            className="wallet-modal wallet-modal-sheet relative mx-3 mb-0 w-full max-w-md rounded-t-[28px] border p-5 sm:mb-6 sm:rounded-[28px]"
            style={{ paddingBottom: 'max(1.25rem, calc(env(safe-area-inset-bottom) + 0.75rem))' }}
          >
            {/* Native-style drag handle — signals "this is a dismissible
                sheet" the way iOS/Android bottom sheets do, hidden on the
                sm:+ centered-modal variant where it doesn't apply. */}
            <div className="mb-3 flex justify-center sm:hidden">
              <span className="wallet-modal-handle h-1 w-9 rounded-full" />
            </div>
            <div className="mb-3 flex items-center justify-between">
              <div>
                <div className="wallet-row-label text-sm font-semibold">Open in a wallet app</div>
                <div className="wallet-row-eyebrow mt-1 text-[10px] font-semibold uppercase tracking-[0.18em]">Mobile sign-in handoff</div>
              </div>
              <button
                type="button"
                className="wallet-modal-close flex h-8 w-8 shrink-0 items-center justify-center rounded-full border text-slate-300 transition-colors hover:text-[color:var(--brand-text)] active:scale-95"
                onClick={() => setShowWalletBrowser(false)}
                aria-label="Close"
              >
                ✕
              </button>
            </div>
            <p className="wallet-row-eyebrow mb-4 text-[13px] leading-relaxed">
              This opens the current page inside your wallet’s built-in browser (best reliability on mobile).
            </p>
            <div className="grid grid-cols-1 gap-2">
              <button
                type="button"
                className="wallet-row flex items-center gap-3.5 rounded-2xl border px-3.5 py-3 text-left transition-all active:scale-[0.985]"
                onClick={() => {
                  setShowWalletBrowser(false);
                  deepLinkWithFallback(buildMetaMaskDeepLink(window.location.href), "MetaMask");
                }}
              >
                <span className="flex h-9 w-9 shrink-0 items-center justify-center rounded-xl" style={{ background: "rgba(246, 133, 27, 0.16)", color: "#FFB463" }}>
                  <IconDiamond />
                </span>
                <span>
                  <span className="wallet-row-label block text-sm font-semibold">MetaMask</span>
                  <span className="wallet-row-eyebrow mt-0.5 block text-[11px]">Launch the current sign-in page inside MetaMask.</span>
                </span>
              </button>
              <button
                type="button"
                className="wallet-row flex items-center gap-3.5 rounded-2xl border px-3.5 py-3 text-left transition-all active:scale-[0.985]"
                onClick={() => {
                  setShowWalletBrowser(false);
                  deepLinkWithFallback(buildCoinbaseDeepLink(window.location.href), "Coinbase Wallet");
                }}
              >
                <span className="flex h-9 w-9 shrink-0 items-center justify-center rounded-xl" style={{ background: "rgba(0, 82, 255, 0.16)", color: "#6FA0FF" }}>
                  <IconRing />
                </span>
                <span>
                  <span className="wallet-row-label block text-sm font-semibold">Coinbase Wallet</span>
                  <span className="wallet-row-eyebrow mt-0.5 block text-[11px]">Open directly in Coinbase Wallet for approval and return.</span>
                </span>
              </button>
              <button
                type="button"
                className="wallet-row flex items-center gap-3.5 rounded-2xl border px-3.5 py-3 text-left transition-all active:scale-[0.985]"
                onClick={() => {
                  setShowWalletBrowser(false);
                  deepLinkWithFallback(buildTrustWalletDeepLink(window.location.href), "Trust Wallet");
                }}
              >
                <span className="flex h-9 w-9 shrink-0 items-center justify-center rounded-xl" style={{ background: "rgba(16, 185, 129, 0.16)", color: "#5EEAC0" }}>
                  <IconShieldSmall />
                </span>
                <span>
                  <span className="wallet-row-label block text-sm font-semibold">Trust Wallet</span>
                  <span className="wallet-row-eyebrow mt-0.5 block text-[11px]">Use Trust Wallet&apos;s browser for the most reliable mobile flow.</span>
                </span>
              </button>
            </div>
            <div className="wallet-status mt-4 rounded-xl border px-3.5 py-3 text-[13px] leading-relaxed">
              Tip: If nothing happens, make sure the wallet app is installed and unlocked, then try again.
            </div>
          </div>
        </div>
      )}

      <p className="wallet-row-eyebrow px-1 text-center text-[13px] leading-relaxed">
        Mobile tip: In Safari/Chrome, use “Open in wallet app” for the smoothest connection.
      </p>

      <style jsx>{`
        .wallet-login-options {
          font-family: var(--font-body), "Segoe UI", sans-serif;
        }

        .wallet-row {
          border-color: rgba(148, 163, 184, 0.16);
          background: rgba(255, 255, 255, 0.025);
        }

        .wallet-row:hover:not(:disabled) {
          border-color: rgba(0, 245, 255, 0.38);
          background: rgba(0, 245, 255, 0.05);
          transform: translateY(-1px);
        }

        .wallet-row:disabled {
          cursor: not-allowed;
        }

        .wallet-row-active {
          border-color: rgba(0, 245, 255, 0.5) !important;
          background: rgba(0, 245, 255, 0.08) !important;
        }

        .wallet-row-disabled {
          opacity: 0.55;
        }

        .wallet-row-label {
          /* Soft brand off-white rather than pure #fff — pure white at this
             density (small, bolded list labels) reads harsh/glary; the
             muted-but-crisp brand text token is the same one used for
             headings elsewhere in the app, just applied at a lighter
             weight here so it still sits clearly above the eyebrow text. */
          color: var(--brand-text, #eaf0ff);
          letter-spacing: 0.005em;
        }

        .wallet-row-eyebrow {
          color: #c3cbe0;
        }

        .wallet-row-soon {
          color: #cbd5e1;
          border-color: rgba(148, 163, 184, 0.3);
          background: rgba(148, 163, 184, 0.1);
        }

        .wallet-row-tag {
          color: #7fe3ff;
          border-color: rgba(0, 245, 255, 0.35);
          background: rgba(0, 245, 255, 0.1);
        }

        .wallet-status {
          border-color: rgba(0, 245, 255, 0.3);
          background: rgba(0, 245, 255, 0.08);
          color: #d7f4ff;
        }

        .wallet-modal {
          border-color: rgba(0, 245, 255, 0.25);
          background: linear-gradient(180deg, rgba(14, 26, 50, 0.99), rgba(6, 12, 26, 1));
          box-shadow: 0 28px 64px -32px rgba(0, 0, 0, 0.98);
        }

        .wallet-modal-handle {
          background: rgba(148, 163, 184, 0.35);
        }

        .wallet-modal-close {
          border-color: rgba(148, 163, 184, 0.22);
          background: rgba(255, 255, 255, 0.04);
        }

        .wallet-modal-close:hover {
          border-color: rgba(148, 163, 184, 0.4);
        }

        .wallet-modal-sheet {
          animation: wallet-sheet-rise 0.28s cubic-bezier(0.16, 1, 0.3, 1) both;
        }

        .wallet-modal-backdrop {
          animation: wallet-fade-in 0.2s ease both;
        }

        @keyframes wallet-sheet-rise {
          from {
            opacity: 0;
            transform: translateY(16px);
          }
          to {
            opacity: 1;
            transform: translateY(0);
          }
        }

        @keyframes wallet-fade-in {
          from { opacity: 0; }
          to { opacity: 1; }
        }

        @media (prefers-reduced-motion: reduce) {
          .wallet-modal-sheet,
          .wallet-modal-backdrop {
            animation: none;
          }
        }
      `}</style>
    </div>
  );
}
